import React, { useEffect, useState } from "react";
import { Box, Container, Grid, Skeleton } from "@mui/material";
import { useHomeController } from "./context/home-controller";
import SectionBanner from "./sections/section-banner";
import SectionNews from "./sections/section-news";
import SectionAccess from "./sections/section-access";
import SectionImportantLinks from "./sections/section-important-links";
import SectionPublicationsTabs from "./sections/section-publications-tabs";
import { CustomHome, DEFAULT_NEWS_QUANTITY } from "./custom-home";
import { useGlobalState } from "../global/context/global-context";
import { HomeSectionType } from "../../model/base/response/portal-home-response";

export function HomeComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { getData } = useHomeController();
    // Customização carregada junto com o portal (AppInitializer); sem customização segue a home padrão
    const { portalHome } = useGlobalState();
    const customSections = portalHome?.isCustomized ? portalHome.sections || [] : [];
    const isCustomized = customSections.length > 0;

    useEffect(() => {
        const newsQuantities = customSections
            .filter((section) => section.type === HomeSectionType.Noticias)
            .map((section) => section.itemsQuantity || DEFAULT_NEWS_QUANTITY);

        const onInit = async () => {
            await getData(Math.max(DEFAULT_NEWS_QUANTITY, ...newsQuantities));
            setInitialLoading(false);
        };
        onInit();
    }, []);

    return (
        <React.Fragment>
            {initialLoading ? (
                <HomeSkeleton />
            ) : isCustomized ? (
                <>
                    {/* Carrossel do topo (tela de Banners do APM); a seção Banners da home é banner de corpo */}
                    <SectionBanner />
                    <CustomHome sections={customSections} />
                </>
            ) : (
                <>
                    <SectionBanner />
                    <SectionAccess variant="subtle" />
                    <SectionImportantLinks variant="plain" />
                    <SectionPublicationsTabs variant="subtle" />
                    <SectionNews variant="plain" />
                </>
            )}
        </React.Fragment>
    );
}

/** Esqueleto no formato final: hero na largura do container + destaque e lista de notícias. */
function HomeSkeleton() {
    return (
        <Box aria-busy="true" aria-label="Carregando página inicial">
            <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 } }}>
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{ width: '100%', height: 'auto', aspectRatio: { xs: '4 / 3', sm: '16 / 6', lg: '16 / 5' }, minHeight: 240, maxHeight: 560 }}
                />
            </Container>
            <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
                <Skeleton variant="text" sx={{ width: 280, maxWidth: '70%', fontSize: '1.75rem', mb: 4 }} />
                <Grid container spacing={{ xs: 3, md: 5 }}>
                    <Grid item xs={12} md={7}>
                        <Skeleton variant="rounded" sx={{ width: '100%', height: 'auto', aspectRatio: '16 / 9', borderRadius: 3 }} />
                        <Skeleton variant="text" sx={{ fontSize: '1.625rem', mt: 2 }} />
                        <Skeleton variant="text" sx={{ fontSize: '1.625rem', width: '60%' }} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        {[0, 1, 2].map((item) => (
                            <Box key={item} sx={{ display: 'flex', gap: 2, py: 2 }}>
                                <Skeleton variant="rounded" sx={{ width: 128, height: 96, flexShrink: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="40%" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" width="80%" />
                                </Box>
                            </Box>
                        ))}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
